import type { NotificationPlacement } from 'ant-design-vue'
import { notification, Result, Button } from 'ant-design-vue'
import { h } from 'vue'
import { getI18nTranslate } from '@/utils/i18nUtils'

let maskElement: HTMLElement | null = null
let enterKeyHandler: ((event: KeyboardEvent) => void) | null = null

/** 創建遮罩層 */
const createMask = (onEnter: () => void) => {
  if (maskElement) return

  // notification 會置中顯示重要結果，額外遮罩可阻止背景頁面被誤點
  maskElement = document.createElement('div')
  maskElement.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.45);
    z-index: 1000;
  `
  document.body.appendChild(maskElement)

  // 遮罩期間背景表單（如登入頁）的輸入框仍保有焦點，Enter 會被瀏覽器當成表單送出；
  // 用 capture 階段攔截並 preventDefault，改為觸發確認按鈕關閉提示
  enterKeyHandler = (event: KeyboardEvent) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    event.stopPropagation()
    onEnter()
  }
  document.addEventListener('keydown', enterKeyHandler, true)
}

/** 移除遮罩層 */
const removeMask = () => {
  if (maskElement) {
    document.body.removeChild(maskElement)
    maskElement = null
  }

  if (enterKeyHandler) {
    document.removeEventListener('keydown', enterKeyHandler, true)
    enterKeyHandler = null
  }
}

/** 通知彈窗相關邏輯 */
export const useNotification = () => {
  /** 關閉通知 */
  const closeNotification = () => {
    notification.destroy()
    removeMask()
  }

  /**
   * 開啟需要明確確認的 notification。
   * @remarks 這支 composable 用於註冊驗證、重大錯誤或流程完成提示，
   *          因此預設保留長時間顯示，直到使用者點擊按鈕或手動關閉。
   */
  const openNotification = (
    message: {
      title?: string
      subTitle: string
    },
    status: 'success' | 'error' | 'info' | 'warning' = 'success',
    placement: NotificationPlacement = 'top',
    callback: () => void = closeNotification,
    options: {
      hasIcon: boolean
      hasClose?: boolean
    } = {
      hasIcon: true,
      hasClose: true
    }
  ) => {
    createMask(callback)

    // duration 設很長，讓 notification 近似 modal 的確認體驗，而不是一般自動消失提醒
    notification.open({
      message: '',
      description: h(Result, {
        class: ['status-notification', !options.hasIcon ? 'no-icon' : ''],
        status: status,
        icon: options.hasIcon ? undefined : null,
        title: [
          h('h5', { class: 'title' }, message.title),
          h('p', { class: 'sub-title' }, message.subTitle)
        ],
        extra: h(
          Button,
          {
            type: 'primary',
            class: ['notification-button', `${status}`],
            onClick: callback
          },
          getI18nTranslate('Common.Notification.Button')
        )
      }),
      placement,
      top: placement === 'top' ? '50%' : undefined,
      style: {
        transform: placement === 'top' ? 'translateY(-50%)' : undefined,
        zIndex: 1001
      },
      closeIcon: options.hasClose ? undefined : null,
      duration: 300,
      // 無論是按按鈕、點關閉或 destroy，都必須把遮罩一起清掉
      onClose: removeMask
    })
  }

  return {
    closeNotification,
    openNotification
  }
}
